import { useId } from 'react';
import { Link } from 'react-router';
import { spendingAccounts } from '@tahi/core';
import {
  setAutoDeduct,
  setDefaultAccount,
  setDefaultFund,
  setUseFunds,
} from '../../../data/commands/budget';
import { useCommandEnv } from '../../../data/hooks';
import { Chip, ScreenHeader } from '../../../ui';
import { Switch } from '../../../ui/Switch';
import { copy } from '../copy';
import { useAccounts, useBudgetSettings, useFunds } from '../hooks';
import { CARD, FOCUS, SCREEN } from '../styles';

const BACK = { to: '/m/budget', label: copy.moduleName };

function AccountPick({
  title,
  help,
  value,
  accounts,
  onPick,
}: {
  title: string;
  help: string;
  value: string | null;
  accounts: { id: string; name: string }[];
  onPick(id: string | null): void;
}) {
  return (
    <section className={CARD}>
      <h2 className="text-[20px] font-bold text-fg">{title}</h2>
      <p className="mt-1 text-sm text-muted">{help}</p>
      <div role="group" aria-label={title} className="mt-2 flex flex-wrap gap-2">
        <Chip selected={value === null} aria-pressed={value === null} onClick={() => onPick(null)}>
          {copy.noneOption}
        </Chip>
        {accounts.map((a) => (
          <Chip
            key={a.id}
            selected={value === a.id}
            aria-pressed={value === a.id}
            onClick={() => onPick(a.id)}
          >
            {a.name}
          </Chip>
        ))}
      </div>
    </section>
  );
}

export function BudgetSettingsScreen() {
  const env = useCommandEnv();
  const settings = useBudgetSettings();
  const funds = useFunds() ?? [];
  const accounts = useAccounts() ?? [];
  const helpId = useId();
  const limitsHelpId = useId();

  if (!settings) {
    return (
      <main className={SCREEN}>
        <ScreenHeader title={copy.settingsTitle} back={BACK} />
        <p className="text-muted">{copy.loading}</p>
      </main>
    );
  }
  const fallback = funds.find((f) => f.id === settings.defaultFundId);
  const hint = !settings.autoDeduct
    ? copy.autoDeductOff
    : fallback
      ? copy.autoDeductOn(fallback.name)
      : copy.autoDeductNoFund;

  return (
    <main className={SCREEN}>
      <ScreenHeader title={copy.settingsTitle} back={BACK} />
      {accounts.length === 0 ? (
        <section className={CARD}>
          <p className="text-sm text-muted">
            {copy.addYourAccounts}{' '}
            <Link to="/m/budget/accounts/new" className={`text-primary ${FOCUS}`}>
              {copy.addAccount}
            </Link>
          </p>
        </section>
      ) : (
        <>
          <AccountPick
            title={copy.payFrom}
            help={copy.payFromHelp}
            value={settings.defaultAccountId}
            accounts={spendingAccounts(accounts)}
            onPick={(id) => void setDefaultAccount(env, 'pay', id)}
          />
          <AccountPick
            title={copy.receiveInto}
            help={copy.receiveIntoHelp}
            value={settings.incomeAccountId}
            accounts={accounts}
            onPick={(id) => void setDefaultAccount(env, 'receive', id)}
          />
        </>
      )}

      <section className={CARD}>
        <div className="flex items-center justify-between gap-3">
          <span className="text-fg">{copy.useFunds}</span>
          <Switch
            label={copy.useFunds}
            checked={settings.useFunds}
            describedBy={limitsHelpId}
            onCheckedChange={(on) => void setUseFunds(env, on)}
          />
        </div>
        <p id={limitsHelpId} className="mt-2 text-sm text-muted">
          {copy.useFundsHelp}
        </p>
        {settings.useFunds ? (
          <Link
            to="/m/budget/funds"
            className={`mt-2 inline-flex min-h-11 items-center text-primary ${FOCUS}`}
          >
            {copy.funds}
          </Link>
        ) : null}
      </section>

      {settings.useFunds ? (
        <>
          <section className={CARD}>
            <div className="flex items-center justify-between gap-3">
              <span className="text-fg">{copy.autoDeductLabel}</span>
              <Switch
                label={copy.autoDeductLabel}
                checked={settings.autoDeduct}
                describedBy={helpId}
                onCheckedChange={(on) => void setAutoDeduct(env, on)}
              />
            </div>
            <p id={helpId} className="mt-2 text-sm text-muted">
              {copy.autoDeductHelp}
            </p>
            <p className="mt-1 text-sm text-muted">{hint}</p>
          </section>
          <section className={CARD}>
            <h2 className="text-[20px] font-bold text-fg">{copy.defaultFund}</h2>
            <div role="group" aria-label={copy.defaultFund} className="mt-2 flex flex-wrap gap-2">
              <Chip
                selected={settings.defaultFundId === null}
                aria-pressed={settings.defaultFundId === null}
                onClick={() => void setDefaultFund(env, null)}
              >
                {copy.noDefaultFund}
              </Chip>
              {funds.map((f) => (
                <Chip
                  key={f.id}
                  selected={settings.defaultFundId === f.id}
                  aria-pressed={settings.defaultFundId === f.id}
                  onClick={() => void setDefaultFund(env, f.id)}
                >
                  {f.name}
                </Chip>
              ))}
            </div>
          </section>
        </>
      ) : null}
    </main>
  );
}
